import { graphql } from '@/gql';

// --- Auth ---

export const GET_PROFILE = graphql(`
  query GetProfile {
    me {
      id
      username
      email
      gender
      roles
      pictureUrl
      isProfileComplete
      isStravaConnected
      isGoogleConnected
    }
  }
`);

export const GET_STRAVA_AUTH_URL = graphql(`
  query GetStravaAuthUrl {
    stravaAuthUrl
  }
`);

export const GET_GOOGLE_AUTH_URL = graphql(`
  query GetGoogleAuthUrl {
    googleAuthUrl
  }
`);

export const CHECK_USERNAME_AVAILABILITY = graphql(`
  query CheckUsernameAvailability($username: String!) {
    checkUsernameAvailability(username: $username)
  }
`);

export const GET_PROFILE_FOR_EDIT = graphql(`
    query GetProfileForEdit {
        me {
            id
            username
            email
            firstName
            lastName
            gender
            birthDate
        }
    }
`);

// --- Races ---

export const LIST_RACES = graphql(`
  query ListRaces($pagination: PaginationInput!, $filter: RaceFilterInput, $sort: RaceSortInput) {
    listRaces(pagination: $pagination, filter: $filter, sort: $sort) {
      items {
        id
        name
        raceFormat
        bettingDeadline
        status
        runnerCount
        bettorCount
        hasCurrentUserBet
        currentUserScore
        isOfficial
        isByUtmb
        isAutoSyncAvailable
      }
      totalCount
      page
      hasNextPage
    }
  }
`);

export const RACES_TO_BET = graphql(`
  query RacesToBet($pagination: PaginationInput!, $sort: RaceSortInput) {
    listRaces(pagination: $pagination, filter: { status: OPEN, hasUserBet: false }, sort: $sort) {
      items {
        id
        name
        raceFormat
        bettingDeadline
        status
        runnerCount
        bettorCount
        isOfficial
        isByUtmb
        isAutoSyncAvailable
      }
      totalCount
      hasNextPage
    }
  }
`);

export const RACES_MY_OPEN_BETS = graphql(`
  query RacesMyOpenBets($pagination: PaginationInput!, $sort: RaceSortInput) {
    listRaces(pagination: $pagination, filter: { status: OPEN, hasUserBet: true }, sort: $sort) {
      items {
        id
        name
        raceFormat
        bettingDeadline
        status
        runnerCount
        bettorCount
        isOfficial
        isByUtmb
        isAutoSyncAvailable
      }
      totalCount
      hasNextPage
    }
  }
`);

export const RACES_MY_RESULTS = graphql(`
  query RacesMyResults($pagination: PaginationInput!, $sort: RaceSortInput) {
    listRaces(pagination: $pagination, filter: { status: ENDED, hasUserBet: true }, sort: $sort) {
      items {
        id
        name
        raceFormat
        bettingDeadline
        status
        runnerCount
        bettorCount
        currentUserScore
        isOfficial
        isByUtmb
        isAutoSyncAvailable
      }
      totalCount
      hasNextPage
    }
  }
`);

export const GET_RACE_DETAILS = graphql(`
    query GetRaceDetails($id: ID!) {
        race(id: $id) {
            id
            name
            description
            raceFormat
            distance
            elevationGain
            status
            visibility
            inviteCode
            bettingDeadline
            createdBy {
                id
                username
                pictureUrl
            }
            invitedUsers {
                user {
                    id
                    username
                    pictureUrl
                }
                role
            }
            isOfficial
            isUtmbRace
            isByUtmb
            isAutoSyncAvailable
            hasCurrentUserBet
            utmbLiveUrl
            pictureUri
            startDate
            closingMessage
            bettingConfig { tokenCount }
            runners {
                id
                utmbId
                utmbUri
                name
                utmbIndex
                gender
                pictureUri
                nationality
                ageGroup
                result
                scratchResult
                live {
                    bib
                    status
                    ranking {
                        scratch
                        gender
                    }
                }
                effectiveIndex
                dnfRatio
                longestFinishedRaceKm
                formatStats {
                    format
                    index
                    finishedRaces
                }
            }
        }
    }
`);

// --- Live Tracking ---

export const GET_LIVE_TRACKING = graphql(`
    query GetLiveTracking($input: LiveTrackingInput!) {
        liveTracking(input: $input) {
            runners {
                fullname
                utmbUri
                bib
                photo
                index
                nationality
                status
                sexRanking
                scratchRanking
            }
            currentUserScoreBreakdown {
                runners {
                    utmbUri
                    predictedPosition
                    score
                    tokens
                    gender
                    livePosition
                    status
                }
                bonuses {
                    type
                    points
                    gender
                }
                totalScore
            }
            fetchedAt
        }
    }
`);

// --- Post-Race Analysis ---

export const GET_POST_RACE_ANALYSIS = graphql(`
    query GetPostRaceAnalysis($raceId: ID!) {
        postRaceAnalysis(raceId: $raceId) {
            totalBettors
            highlights {
                biggestSurprises {
                    runnerName
                    gender
                    expectedPosition
                    actualPosition
                    deviation
                }
                bestPrediction {
                    username
                    runnerName
                    expectedPosition
                    predictedPosition
                    actualPosition
                    score
                }
                surpriseDnfs {
                    runnerName
                    gender
                    expectedPosition
                    predictedDnfCount
                    totalBettors
                }
            }
            consensusVsReality {
                runnerName
                gender
                expectedPosition
                averagePredictedPosition
                actualPosition
            }
            scoreDistribution {
                max
                min
                average
                median
                totalBettors
            }
            userRecap {
                totalScore
                bestPrediction {
                    runnerName
                    predictedPosition
                    actualPosition
                    score
                }
                worstPrediction {
                    runnerName
                    predictedPosition
                    actualPosition
                    score
                }
                exactPredictions
                closePredictions
                totalPredictions
                accuracyPercentile
                rankingDetails {
                    runnerName
                    gender
                    predictedPosition
                    actualPosition
                    score
                    tokens
                    status
                    consensusPosition
                }
                bonuses {
                    type
                    points
                    gender
                }
                scoreByGender {
                    gender
                    score
                    count
                }
            }
        }
    }
`);

export const GET_LIVE_LEADERBOARD = graphql(`
    query GetLiveLeaderboard($input: LiveLeaderboardInput!) {
        liveLeaderboard(input: $input) {
            entries {
                user {
                    id
                    username
                    pictureUrl
                }
                totalScore
                scoredRunners
                totalRunners
            }
            currentUserBreakdown {
                runners {
                    utmbUri
                    predictedPosition
                    score
                    tokens
                    gender
                    livePosition
                    status
                }
                bonuses {
                    type
                    points
                    gender
                }
                totalScore
            }
        }
    }
`);

export const GET_RACE_FOR_EDIT = graphql(`
    query GetRaceForEdit($id: ID!) {
        race(id: $id) {
            id
            name
            description
            raceFormat
            distance
            elevationGain
            bettingDeadline
            visibility
            totalRunners
            isOfficial
            isUtmbRace
            bettingConfig { tokenCount }
            bettorCount
        }
    }
`);

export const GET_CONTEXT_RACE = graphql(`
    query GetContextRace($raceId: ID!) {
        race(id: $raceId) {
            id
            totalRunners
            runnerCount
            runners {
                id
                utmbUri
            }
        }
    }
`);

// --- Bets ---

export const MY_BETS = graphql(`
    query MyBets($pagination: PaginationInput, $filter: BetFilterInput, $sort: BetSortInput) {
        myBets(pagination: $pagination, filter: $filter, sort: $sort) {
            items {
                id
                score
                createdAt
                race {
                    id
                    name
                    status
                    bettingDeadline
                }
                rankings {
                    position
                    gender
                    score
                    tokens
                    status
                    runner {
                        id
                        name
                        utmbIndex
                        result
                    }
                }
                bonuses {
                    type
                    points
                    gender
                }
            }
            totalCount
            page
            hasNextPage
        }
    }
`);

// --- Bettors (lightweight: who bet on a race) ---

export const RACE_BETTORS = graphql(`
    query RaceBettors($raceId: ID!, $pagination: PaginationInput) {
        raceBettors(raceId: $raceId, pagination: $pagination) {
            items {
                user { id username pictureUrl }
                bet { genders }
            }
            totalCount
        }
    }
`);

// --- Leaderboard ---

export const RACE_LEADERBOARD = graphql(`
    query RaceLeaderboard($raceId: ID!, $pagination: PaginationInput) {
        raceLeaderboard(raceId: $raceId, pagination: $pagination) {
            items {
                totalScore
                user { id username pictureUrl }
                bet {
                    id
                    score
                    rankings {
                        position
                        gender
                        score
                        tokens
                        status
                        runner { id name result }
                    }
                    bonuses {
                        type
                        points
                        gender
                    }
                }
            }
            totalCount
            page
            hasNextPage
        }
    }
`);

// --- Home Dashboard ---

export const HOME_STATS = graphql(`
    query HomeStats {
        me {
            id
            username
            stats {
                totalBets
                totalRaces
                totalScore
                racesCreated
            }
        }
    }
`);

export const HOME_OPEN_RACES = graphql(`
    query HomeOpenRaces($pagination: PaginationInput!, $filter: RaceFilterInput, $sort: RaceSortInput) {
        listRaces(pagination: $pagination, filter: $filter, sort: $sort) {
            items {
                id
                name
                raceFormat
                bettingDeadline
                status
                bettorCount
                hasCurrentUserBet
                isOfficial
                isByUtmb
                isAutoSyncAvailable
                runners {
                    id
                }
            }
            totalCount
            hasNextPage
        }
    }
`);

export const HOME_MY_BETS = graphql(`
    query HomeMyBets($pagination: PaginationInput, $filter: BetFilterInput, $sort: BetSortInput) {
        myBets(pagination: $pagination, filter: $filter, sort: $sort) {
            items {
                id
                score
                createdAt
                race {
                    id
                    name
                    status
                    bettingDeadline
                }
                rankings {
                    position
                    gender
                    runner {
                        id
                        name
                    }
                }
            }
            totalCount
        }
    }
`);

export const HOME_LEADERBOARD = graphql(`
    query HomeLeaderboard($pagination: PaginationInput) {
        globalLeaderboard(pagination: $pagination) {
            items {
                user {
                    id
                    username
                    pictureUrl
                }
                totalScore
                raceCount
            }
            totalCount
            currentUserRank
            currentUserScore
        }
    }
`);

// --- Ranking ---

export const RANKING_GLOBAL_LEADERBOARD = graphql(`
    query RankingGlobalLeaderboard($pagination: PaginationInput) {
        globalLeaderboard(pagination: $pagination) {
            items {
                user {
                    id
                    username
                    pictureUrl
                }
                totalScore
                raceCount
            }
            totalCount
            hasNextPage
            page
            currentUserRank
            currentUserScore
        }
    }
`);

export const MY_RACE_RESULTS = graphql(`
    query MyRaceResults($pagination: PaginationInput) {
        myRaceResults(pagination: $pagination) {
            items {
                race {
                    id
                    name
                    bettingDeadline
                    isOfficial
                }
                totalScore
                rank
                totalBettors
            }
            totalCount
            hasNextPage
            page
        }
    }
`);

// --- Scoring ---

export const POTENTIAL_SCORE = graphql(`
    query PotentialScore($raceId: ID!, $runnerId: ID!, $position: Int!) {
        potentialScore(raceId: $raceId, runnerId: $runnerId, position: $position) {
            score
            surpriseMultiplier
        }
    }
`);

// --- UTMB ---

export const SEARCH_UTMB_RACES = graphql(`
    query SearchUtmbRaces($search: String, $categories: [RaceFormat!], $countryCodes: [String!], $limit: Int) {
        searchUtmbRaces(search: $search, categories: $categories, countryCodes: $countryCodes, limit: $limit) {
            uri
            name
            eventName
            startDate
            startPlace
            distance
            elevationGain
            category
        }
    }
`);

// --- Runners ---

export const SEARCH_RUNNERS = graphql(`
    query SearchRunners($input: SearchRunnersInput!) {
        searchRunners(searchRunnersInput: $input) {
            name
            utmbId
            utmbUri
            utmbIndex
            gender
            ageGroup
            nationality
            pictureUri
        }
    }
`);
